import type { PropsWithChildren, FunctionComponent } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'
import { type Partner } from '../../../__codegen__/api'
import { useDashMessages } from '../../../intl/intl.hooks'
import { PartnerType } from '../../../exports/utils'
import { MenuOption } from './MenuOption'

interface Props {
  partner: Pick<Partner, 'id' | 'name' | 'partnerType'> & {
    retailers: { id: string; name?: string; logo: { url: string } }[]
  }
  onPartnerRemoveFromRecents?: (id: string) => void
  debouncedSearchFilter?: string
}

const Heavy = styled.span({ fontWeight: 600 })

const escapeRegExp = (string: string | undefined) => {
  if (!string) return ''
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const createHighlightedTitleElement = (
  partner: Pick<Partner, 'id' | 'name' | 'partnerType'>,
  debouncedSearchFilter: string | undefined
) => {
  const escapedFilter = escapeRegExp(debouncedSearchFilter)
  const match = partner?.name?.match(new RegExp(escapedFilter, 'i'))
  const matchIndex = match?.index
  const matchLength = match?.[0]?.length

  if (!match) {
    return partner?.name
  }

  const label = (
    <>
      {partner?.name?.slice(0, matchIndex)}
      {/* @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check */}
      <Heavy>{partner?.name?.slice(matchIndex, matchIndex + matchLength)}</Heavy>
      {/* @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check */}
      {partner?.name?.slice(matchIndex + matchLength)}
    </>
  )

  return label
}

export const PartnerOption: FunctionComponent<PropsWithChildren<Props>> = ({
  partner,
  onPartnerRemoveFromRecents,
  debouncedSearchFilter,
}) => {
  const message = useDashMessages({
    banners: 'selectPartner.banners',
    searchBanners: 'selectPartner.searchResults.banners',
    searchMatch: 'selectPartner.searchResults.match',
  })

  const escapedFilter = escapeRegExp(debouncedSearchFilter)

  const numMatches = debouncedSearchFilter
    ? partner.retailers.filter(
        retailer =>
          retailer.id === debouncedSearchFilter ||
          retailer?.name?.match(new RegExp(escapedFilter, 'i'))
      ).length
    : null

  const numMatchText = numMatches ? (
    <>
      <Heavy>
        {numMatches} {message.searchBanners}
      </Heavy>{' '}
      {message.searchMatch} <Heavy>“{debouncedSearchFilter}”</Heavy>
    </>
  ) : null

  const logos = partner.retailers.map(e => e?.logo?.url).slice(0, 4)

  const label = useMemo(
    () => createHighlightedTitleElement(partner, debouncedSearchFilter),
    [partner, debouncedSearchFilter]
  )

  const labelTag = useMemo(() => {
    switch (partner.partnerType) {
      case PartnerType.IDP:
        return 'IDP'
      case PartnerType.Retailer:
        return 'Retailer'
      default:
        return null
    }
  }, [partner.partnerType])

  const numBanners = partner.retailers.length < 200 ? partner.retailers.length : '200+'

  return (
    <MenuOption
      label={label}
      // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
      labelTag={labelTag}
      // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
      subLabel={numMatchText}
      // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
      farRightSubLabel={
        partner.partnerType === PartnerType.Retailer && `${numBanners} ${message.banners}`
      }
      logos={logos}
      heavyLabel={!debouncedSearchFilter}
      onClose={
        onPartnerRemoveFromRecents &&
        (e => {
          onPartnerRemoveFromRecents(partner.id)
          e.stopPropagation()
        })
      }
    />
  )
}
